import { Component, computed, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import type { StripeElements } from '@stripe/stripe-js';
import { catchError, map, of, switchMap, take } from 'rxjs';
import { AddressesService } from '../../core/services/addresses.service';
import { AnalyticsService } from '../../core/services/analytics.service';
import { AuthService } from '../../core/services/auth.service';
import { MessagingService } from '../../core/services/messaging.service';
import { ProductsService } from '../../core/services/products.service';
import { SeoService } from '../../core/services/seo.service';
import { SignupPromptService } from '../../core/services/signup-prompt.service';
import { StripeClientService } from '../../core/services/stripe-client.service';
import { ToastService } from '../../core/services/toast.service';
import { TransactionsService } from '../../core/services/transactions.service';
import { WishlistService } from '../../core/services/wishlist.service';
import { formatCondition, formatPriceCents } from '../../core/utils/currency';
import { productStatusBadgeClass, productStatusLabel } from '../../core/utils/product-status';
import type { Address } from '../../core/models/address.model';
import type { DeliveryMethod, ProductDetail } from '../../core/models/product.model';
import type {
  PaymentIntentQuote,
  Transaction,
  TransactionStatus,
  TransactionType,
} from '../../core/models/transaction.model';
import { IconComponent, type IconName } from '../../shared/components/icon/icon.component';
import { ProductImageComponent } from '../../shared/components/product-image/product-image.component';
import { SnapCarouselComponent } from '../../shared/components/snap-carousel/snap-carousel.component';
import { StarRatingComponent } from '../../shared/components/star-rating/star-rating.component';

type CheckoutStep = 'details' | 'payment';

interface HighlightSpec {
  label: string;
  value: string;
  icon?: IconName;
  swatch?: string;
}

interface SpecRow {
  label: string;
  value: string;
  swatch?: string;
}

// Mirrors the backend's ACTIVE_BUYER_ORDER_STATUSES (see
// TransactionsService.createPaymentIntent) — a buyer with an order on this
// listing in any of these statuses can't open another one.
const ACTIVE_ORDER_STATUSES: TransactionStatus[] = [
  'PENDING',
  'ACCEPTED',
  'INSPECTION',
  'REFUND_REQUESTED',
];

@Component({
  selector: 'app-product-detail',
  standalone: true,
  imports: [
    RouterLink,
    StarRatingComponent,
    ProductImageComponent,
    SnapCarouselComponent,
    IconComponent,
  ],
  templateUrl: './product-detail.component.html',
})
export class ProductDetailComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly productsService = inject(ProductsService);
  private readonly authService = inject(AuthService);
  private readonly wishlistService = inject(WishlistService);
  private readonly signupPromptService = inject(SignupPromptService);
  private readonly transactionsService = inject(TransactionsService);
  private readonly messagingService = inject(MessagingService);
  private readonly seoService = inject(SeoService);
  private readonly addressesService = inject(AddressesService);
  private readonly stripeClientService = inject(StripeClientService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly toastService = inject(ToastService);

  @ViewChild('paymentElementContainer') paymentElementContainer?: ElementRef<HTMLDivElement>;

  readonly formatPriceCents = formatPriceCents;
  readonly formatCondition = formatCondition;

  readonly loadError = signal(false);
  readonly product = signal<ProductDetail | null>(null);

  readonly isOwner = computed(() => {
    const product = this.product();
    const currentUser = this.authService.currentUser();
    return !!product && !!currentUser && product.sellerId === currentUser.id;
  });

  readonly isSold = computed(() => this.product()?.status === 'RECENTLY_SOLD');

  readonly highlightSpecs = computed<HighlightSpec[]>(() => {
    const product = this.product();
    if (!product) return [];

    const specs: HighlightSpec[] = [
      {
        icon: 'arrowsLeftRight',
        label: 'Tree width',
        value: formatCondition(product.treeWidth),
      },
      { icon: 'cube', label: 'Material', value: formatCondition(product.material) },
      { label: 'Colour', value: product.colour.name, swatch: product.colour.hexSwatch },
      {
        icon: 'mapPin',
        label: 'Location',
        value: `${product.locationSuburb}, ${product.locationState}`,
      },
    ];
    if (product.year) {
      specs.push({ icon: 'calendar', label: 'Year', value: String(product.year) });
    }
    specs.push({ icon: 'tag', label: 'Listing ref', value: product.listingRef });
    return specs;
  });

  readonly fullSpecRows = computed<SpecRow[]>(() => {
    const product = this.product();
    if (!product) return [];

    const rows: SpecRow[] = [{ label: 'Brand', value: product.brand.name }];
    if (product.model) rows.push({ label: 'Model', value: product.model });
    rows.push(
      { label: 'Discipline', value: product.discipline.name },
      { label: 'Seat size', value: `${product.seatSizeInches}"` },
    );
    if (product.flapLength) rows.push({ label: 'Flap length', value: product.flapLength });
    rows.push(
      { label: 'Tree width', value: formatCondition(product.treeWidth) },
      { label: 'Material', value: formatCondition(product.material) },
      { label: 'Colour', value: product.colour.name, swatch: product.colour.hexSwatch },
      { label: 'Condition', value: formatCondition(product.condition) },
    );
    if (product.year) rows.push({ label: 'Year', value: String(product.year) });
    rows.push({
      label: 'Location',
      value: `${product.locationSuburb}, ${product.locationState}`,
    });
    return rows;
  });

  /** The buyer's own still-active order on this listing, if any — set once
   * the product loads (see the constructor). Gates the Buy Now/Make an
   * Offer buttons: a second order on top of one already in progress just
   * confuses the seller and ties up a second card authorization. */
  readonly activeOrder = signal<Transaction | null>(null);

  readonly publishing = signal(false);
  readonly publishError = signal<string | null>(null);
  readonly productStatusLabel = productStatusLabel;
  readonly productStatusBadgeClass = productStatusBadgeClass;

  constructor() {
    this.route.paramMap
      .pipe(
        // A hard refresh restores the in-memory access token via a silent
        // refresh call that resolves after the app starts rendering —
        // without waiting here, an owner's own DRAFT/etc. listing could
        // fetch anonymously and 404 before the session comes back.
        switchMap((params) =>
          this.authService.bootstrap().pipe(
            take(1),
            map(() => params),
          ),
        ),
        map((params) => params.get('id')!),
        switchMap((id) => {
          this.product.set(null);
          this.loadError.set(false);
          this.seoService.setLcpPreload(null);
          return this.fetchProduct(id);
        }),
        takeUntilDestroyed(),
      )
      .subscribe((product) => {
        this.product.set(product);
        this.activeOrder.set(null);

        if (product) {
          const primaryImage = product.images.find((img) => img.isPrimary) ?? product.images[0];
          this.seoService.set({
            title: `${product.title} — ${product.brand.name} ${product.discipline.name} Saddle | Galopa`,
            description: `${product.brand.name} ${product.discipline.name} saddle, ${formatCondition(product.condition)} condition, seat size ${product.seatSizeInches}" — ${formatPriceCents(product.priceAmountCents, product.currency)} on Galopa. Buyer-protected payments, resell for free if it's not right.`,
            image: primaryImage?.url,
          });
          this.seoService.setLcpPreload(primaryImage?.url ?? null);
        }

        const currentUser = this.authService.currentUser();
        if (!product || !currentUser || product.sellerId === currentUser.id) return;

        this.transactionsService.findMine().subscribe((transactions) => {
          const match = transactions.find(
            (t) => t.productId === product.id && ACTIVE_ORDER_STATUSES.includes(t.status),
          );
          this.activeOrder.set(match ?? null);
        });
      });
  }

  private fetchProduct(id: string) {
    return this.productsService.findOne(id).pipe(
      // A DRAFT/removed/other-seller's listing 404s — without this the
      // source observable errors and the page gets stuck on "Loading
      // listing…" forever instead of surfacing an error.
      catchError(() => {
        this.loadError.set(true);
        return of(null);
      }),
    );
  }

  publishListing(): void {
    const product = this.product();
    if (!product || product.status !== 'DRAFT') return;

    this.publishing.set(true);
    this.publishError.set(null);

    this.productsService.publish(product.id).subscribe({
      next: () => {
        this.analyticsService.listingPublished(product.id);
        this.toastService.show(`"${product.title}" is now live — buyers can find it in Shop.`);
        this.fetchProduct(product.id).subscribe((updated) => {
          this.product.set(updated);
          this.publishing.set(false);
        });
      },
      error: (err: unknown) => {
        this.publishing.set(false);
        this.publishError.set(this.extractErrorMessage(err));
      },
    });
  }

  // ---------- Checkout modal state ----------
  readonly checkoutOpen = signal(false);
  readonly checkoutStep = signal<CheckoutStep>('details');
  readonly checkoutType = signal<TransactionType>('BUY_NOW');
  readonly deliveryMethod = signal<DeliveryMethod>('LOCAL_PICKUP');
  readonly offerAudInput = signal('');
  readonly addresses = signal<Address[]>([]);
  readonly selectedAddressId = signal<string | null>(null);
  readonly quote = signal<PaymentIntentQuote | null>(null);
  readonly submitting = signal(false);
  readonly checkoutError = signal<string | null>(null);

  private elements: StripeElements | null = null;

  isSaved(): boolean {
    const product = this.product();
    return !!product && this.wishlistService.isSaved(product.id);
  }

  toggleSave(): void {
    const product = this.product();
    if (!product || this.isOwner()) return;

    if (!this.authService.isAuthenticated()) {
      this.signupPromptService.show('Create a free account to save this saddle.');
      return;
    }

    this.wishlistService.toggle(product.id).subscribe();
  }

  /** Sold listings replace their buy/offer/message CTAs with this one — it
   * hands off to a prefilled Wanted Saddle draft (see
   * AccountWantedAdFormComponent.prefillFromProduct) rather than the soft
   * SignupPromptService nudge used elsewhere on this page, because getting
   * there after signup requires an actual page redirect, not a modal. */
  wantSomethingLikeThis(): void {
    const product = this.product();
    if (!product) return;

    const target = `/account/wanted-ads/new?fromProduct=${product.id}`;
    if (this.authService.isAuthenticated()) {
      this.router.navigateByUrl(target);
      return;
    }
    this.router.navigate(['/signup'], { queryParams: { returnUrl: target } });
  }

  messageSeller(): void {
    const product = this.product();
    if (!product || this.isOwner()) return;

    if (!this.authService.isAuthenticated()) {
      this.signupPromptService.show('Create a free account to message this seller.');
      return;
    }

    this.messagingService.getOrCreate(product.id).subscribe((conversation) => {
      this.router.navigate(['/account/messages'], {
        queryParams: { conversationId: conversation.id },
      });
    });
  }

  // ---------- Checkout flow ----------

  openCheckout(type: TransactionType): void {
    const product = this.product();
    if (!product || this.isOwner() || this.activeOrder()) return;

    if (!this.authService.isAuthenticated()) {
      this.signupPromptService.show(
        type === 'OFFER'
          ? 'Create a free account to make an offer.'
          : 'Create a free account to buy this saddle.',
      );
      return;
    }

    this.checkoutType.set(type);
    this.deliveryMethod.set(product.localPickupAvailable ? 'LOCAL_PICKUP' : 'DELIVERY');
    this.offerAudInput.set('');
    this.selectedAddressId.set(null);
    this.quote.set(null);
    this.checkoutError.set(null);
    this.checkoutStep.set('details');
    this.checkoutOpen.set(true);

    if (product.deliveryAvailable) {
      this.addressesService.findAll().subscribe((items) => {
        this.addresses.set(items);
        const defaultAddress = items.find((a) => a.isDefaultShipping) ?? items[0];
        if (defaultAddress) this.selectedAddressId.set(defaultAddress.id);
      });
    }
  }

  closeCheckout(): void {
    this.checkoutOpen.set(false);
    this.elements = null;
  }

  canProceedToPayment(): boolean {
    if (this.checkoutType() === 'OFFER') {
      const cents = this.offerAmountCents();
      if (cents === null || cents <= 0) return false;
    }
    if (this.deliveryMethod() === 'DELIVERY' && !this.selectedAddressId()) return false;
    return true;
  }

  offerAmountCents(): number | null {
    const raw = this.offerAudInput().replace(/[^0-9.]/g, '');
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  }

  proceedToPayment(): void {
    const product = this.product();
    if (!product || !this.canProceedToPayment()) return;

    this.submitting.set(true);
    this.checkoutError.set(null);

    this.transactionsService
      .createPaymentIntent({
        productId: product.id,
        type: this.checkoutType(),
        offerAmountCents: this.checkoutType() === 'OFFER' ? this.offerAmountCents()! : undefined,
        deliveryMethod: this.deliveryMethod(),
        shippingAddressId:
          this.deliveryMethod() === 'DELIVERY' ? this.selectedAddressId()! : undefined,
      })
      .subscribe({
        next: (quote) => {
          this.quote.set(quote);
          this.submitting.set(false);
          this.checkoutStep.set('payment');
          this.analyticsService.beginCheckout({
            productId: product.id,
            checkoutType: this.checkoutType(),
            amountCents: quote.amountCents,
            currency: quote.currency,
          });
          // Give Angular a tick to render the payment-element container
          // before mounting Stripe Elements into it.
          setTimeout(() => this.mountPaymentElement(), 0);
        },
        error: (err: unknown) => {
          this.submitting.set(false);
          this.checkoutError.set(this.extractErrorMessage(err));
        },
      });
  }

  backToDetails(): void {
    this.checkoutStep.set('details');
    this.elements = null;
  }

  private async mountPaymentElement(): Promise<void> {
    const quote = this.quote();
    const container = this.paymentElementContainer?.nativeElement;
    if (!quote || !container) return;

    const stripe = await this.stripeClientService.getStripe();
    if (!stripe) {
      this.checkoutError.set('Payments are unavailable right now — Stripe failed to load.');
      return;
    }

    this.elements = stripe.elements({ clientSecret: quote.clientSecret });
    const paymentElement = this.elements.create('payment');
    paymentElement.mount(container);
  }

  async submitPayment(): Promise<void> {
    const quote = this.quote();
    if (!quote || !this.elements) return;

    this.submitting.set(true);
    this.checkoutError.set(null);

    const stripe = await this.stripeClientService.getStripe();
    if (!stripe) {
      this.submitting.set(false);
      this.checkoutError.set('Payments are unavailable right now — Stripe failed to load.');
      return;
    }

    const { error } = await stripe.confirmPayment({
      elements: this.elements,
      redirect: 'if_required',
    });

    if (error) {
      this.submitting.set(false);
      this.checkoutError.set(error.message ?? 'Your card could not be authorized.');
      return;
    }

    this.transactionsService.confirm(quote.paymentIntentId).subscribe({
      next: (transaction) => {
        this.submitting.set(false);
        this.checkoutOpen.set(false);
        this.analyticsService.purchase({
          transactionId: transaction.id,
          productId: transaction.productId,
          totalAmountCents: transaction.totalAmountCents,
          currency: transaction.currency,
        });
        this.toastService.show(
          transaction.type === 'OFFER'
            ? "Offer sent — the seller has 48 hours to respond. We'll email you either way."
            : "Request sent — the seller has 48 hours to accept. We'll email you either way.",
        );
        this.router.navigate(['/account/orders']);
      },
      error: (err: unknown) => {
        this.submitting.set(false);
        this.checkoutError.set(this.extractErrorMessage(err));
      },
    });
  }

  private extractErrorMessage(err: unknown): string {
    if (err && typeof err === 'object' && 'error' in err) {
      const body = (err as { error?: { message?: string | string[] } }).error;
      if (body?.message) {
        return Array.isArray(body.message) ? body.message.join(' ') : body.message;
      }
    }
    return 'Something went wrong. Please try again.';
  }
}
